import { IAttendantTemplate, InputType } from "interfaces";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { REGEX_VALID_EMAIL } from "utills/constants";
import { calculateAge } from "utills/helpers";
import { z } from "zod";

export const ParticipantFormSchema = (attendantTemplate: IAttendantTemplate  | null, dob: string | null = null, startDate: string) => {
  const schemaObject: { [key: string]: z.ZodTypeAny } = {};
  Object.keys(attendantTemplate ?? {}).forEach((key: string) => {
    const dobField =  (attendantTemplate?.[key].fields ?? []).find(field => field.key === "dob");
    (attendantTemplate?.[key].fields ?? []).forEach(field => {
      let isRequired = field.require === true;
      const isLegalField = field.key.includes('legal') || field.key.includes("runner_relationship");

      if (isLegalField) {
        if ((!dob || (dob && calculateAge(new Date(dob), get(dobField, "option.by", null) || new Date(startDate)) >= 18))) {
          return;
        }
      }


      let fieldSchema: z.ZodTypeAny = z.any();
      
      switch (field.type) {
        case InputType.CHECKBOX:
          if (isRequired) {
            fieldSchema = z.any().refine(
              (val) => val === "1" || val === true,
              { message: `${field.label_en} must be checked` }
            );
          } else {
            fieldSchema = z.any().optional();
          }
          break;
        case InputType.NUMBER:
          let numSchema = null;
          
          if (isRequired) {
            numSchema = z.string().min(1, "This field is required");
          } else {
            numSchema = z.string().optional();
          }          
          if (field.option) {
            const minValue = get(field.option, "min", undefined);
            const maxValue = get(field.option, "max", undefined);
            if (minValue !== undefined || maxValue !== undefined) {
              numSchema = numSchema.refine(
                (val) => {
                  if (!val) return !isRequired;
                  
                  const numVal = Number(val);
                  if (isNaN(numVal)) return false;
                  
                  if (minValue !== undefined && numVal < minValue) return false;
                  if (maxValue !== undefined && numVal > maxValue) return false;
                  
                  return true;
                },
                {
                  message: `Number must ${
                    minValue !== undefined && maxValue !== undefined
                      ? `be between ${minValue} and ${maxValue}`
                      : minValue !== undefined
                      ? `be equal or bigger than ${minValue}`
                      : `be equal or smaller than ${maxValue}`
                  }`
                }
              );
            }
          }
          
          fieldSchema = numSchema;
          break;
        case InputType.DATE:
          let dateSchema = null;
          
          if (isRequired) {
            dateSchema = z.string().min(1, "This field is required");
          } else {
            dateSchema = z.string().optional();
          }
          
          if (field.validation === "dob" && field.option && !isEmpty(field.option)) {
            const minAge = get(field.option, "min", undefined);
            const maxAge = get(field.option, "max", undefined);
            const byDateStr = get(field.option, "by", undefined);

            if (minAge !== undefined || maxAge !== undefined) {
              dateSchema = dateSchema.refine(
                (val) => {
                  if (!val) return !isRequired;
                  
                  const birthDate = new Date(val);
                  
                  if (isNaN(birthDate.getTime())) return false;
                  
                  const referenceDate = byDateStr ? new Date(byDateStr) : new Date(startDate);
                  const currentAge = calculateAge(birthDate, referenceDate);
                  
                  if (minAge !== undefined && currentAge < minAge) return false;
                  if (maxAge !== undefined && currentAge > maxAge) return false;
                  
                  return true;
                },
                {
                  message: `${field.label_en} must be ${
                    minAge !== undefined && maxAge !== undefined
                      ? `between ${minAge} and ${maxAge}`
                      : minAge !== undefined
                      ? `at least ${minAge}`
                      : `${maxAge} or under`
                  } ${byDateStr ? `as of ${moment(byDateStr).format("DD/MM/YYYY")}` : ''}`
                }
              );
            }
          }
          
          fieldSchema = dateSchema;
          break;

        case InputType.TEXT: {
          let textSchema = null;
          
          if (field.validation === "email") {
            textSchema = z.string().refine(
              (val) => {
                if (!val) return !isRequired;
                
                if (REGEX_VALID_EMAIL.test(val ?? "")) return true;
                
                return false;
              },
              {
                message: "Invalid email address"
              }
            );
          } else if (field.validation === "length" && field.option && field.option) {
            const minLength = get(field.option, "min", undefined);
            const maxLength = get(field.option, "max", undefined);
            
            textSchema = z.string();
            
            if (minLength !== undefined && maxLength !== undefined) {
              textSchema = z.string().min(minLength, `${field.label_en} must be at least ${minLength} characters long`)
                                     .max(maxLength, `${field.label_en} must not exceed ${maxLength} characters`);
            } else if (minLength !== undefined) {
              textSchema = z.string().min(minLength, `${field.label_en} must be at least ${minLength} characters long`);
            } else if (maxLength !== undefined) {
              textSchema = z.string().max(maxLength, `${field.label_en} must not exceed ${maxLength} characters`);
            }
          } else {
            if (isRequired && field.validation !== "length") {
              textSchema = z.string().min(1, "This field is required");
            } else if (isRequired && field.validation === "length" &&  (!field.option || isEmpty(field.option) || get(field.option, "min", undefined) === undefined)) {
              textSchema = z.string().min(1, "This field is required");
            } else if (!isRequired) {
              textSchema = z.string().optional();
            } else {
              textSchema = z.string().optional();
            }
          }
          fieldSchema = textSchema;
          break;
        }
          
        default:             
          if (isRequired) {
            fieldSchema = z.string().min(1, "This field is required");
          } else {
            fieldSchema = z.string().optional();
          };
          break;
      }      
      schemaObject[field.key] = fieldSchema;
      if (field.key.includes("phone") && field.validation === "phone") {
        schemaObject[`${field.key}_area_code`] = z.string().optional()
      }
    });
  })  
  return z.object({
    ...schemaObject
  });
}
